import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import Alert from "components/atoms/alert/Alert";
import Button from "components/atoms/button";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";

import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { apiSpaceUrl } from "utils/urls";
import { isReadonlyUser } from "utils/user";

import axios from "axios";

const FileUploadSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 40px;

    ${(props) => props.isFileDragging && `background-color: var(--color-Base1);`}
`;

const FileUploadText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const FileSearchButtonWrap = styled.div`
    display: flex;
    align-items: center;
    margin-top: 8px;
    flex-wrap: wrap;
    gap: 4px;
`;

const FileSearchButton = styled(Button)`
    margin-top: 12px;
    height: 24px;
    padding: 0 4px;
    border-radius: 4px;
    font-size: 1.2rem;
    background-color: var(--color-Button3);

    &:hover {
        background-color: var(--color-ButtonHover3);
    }
`;

function PDFChatFileUploadView(props) {
    const match = useMatch("/:space_uuid/*");
    const space_uuid = match?.params?.space_uuid ?? "";
    const { setLoading } = useLoading();
    const volumeQuery = useUser();
    const [volumeInfo, setVolumeInfo] = useState({});
    useEffect(() => {
        if (volumeQuery.data) {
            setVolumeInfo({ ...volumeQuery.data.current_space?.volume_info });
        }
    }, [volumeQuery.data]);

    // region: 파일 업로드
    const fileInputRef = useRef(null);
    const [fileUploadStatus, setFileUploadStatus] = useState(null);

    const fileChangeHandler = (e) => {
        if (props.reference?.file) {
            axios.head(props.reference.file).then((res) => {
                if (volumeInfo?.used + e.target.files[0].size > volumeInfo?.max) {
                    Alert("warn", "에러", "파일 용량이 초과되었습니다.");
                    return;
                }
            });
        } else {
            if (volumeInfo?.used + e.target.files[0].size > volumeInfo?.max) {
                Alert("warn", "에러", "파일 용량이 초과되었습니다.");
                return;
            }
        }
        // 50MB 이상의 파일은 업로드 불가
        if (e.target.files[0].size > 50 * Constants.MB) {
            Alert("warn", "에러", "50MB 이상의 파일은 업로드할 수 없습니다.");
            return;
        }
        const { files } = e.target;
        if (files.length > 0) {
            let upload_data = { file: files[0], reference_id: props.reference.id };
            uploadFile(upload_data);
        }
    };

    const uploadFile = (upload_data) => {
        setLoading(true, "PDF 파일을 업로드 중입니다.");
        setFileUploadStatus("loading");
        axios
            .post(apiSpaceUrl("bibliography", space_uuid) + "/file", upload_data, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setLoading(true, `PDF 파일을 업로드 중입니다. (${percentCompleted}%)`);
                    if (percentCompleted === 100) {
                        setLoading(true, "PDF 파일을 저장 중입니다.");
                    }
                },
            })
            .then((res) => {
                volumeQuery.refetch(); // 용량 정보 갱신
                setFileUploadStatus(null);
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
                setFileUploadStatus(null);
                Alert("warn", "에러", error.response.data.file);
            });
    };

    // region: 드래그 & 드롭
    const [isFileDragging, setIsFileDragging] = useState(false);
    const handleFileDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(true);
        }
    };
    const handleFileDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(true);
        }
    };
    const handleFileDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(false);
        }
    };
    const handleFileDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(false);
            if (e.dataTransfer.files) {
                const mockEvent = {
                    target: {
                        files: e.dataTransfer.files,
                    },
                };
                fileChangeHandler(mockEvent);
            }
        }
    };

    return (
        <FileUploadSection
            onDragEnter={handleFileDragEnter}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
            isFileDragging={isFileDragging}
        >
            <FileUploadText style={{ marginBottom: "24px" }}>논문 PDF 파일이 없습니다.</FileUploadText>
            <FileUploadText>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: "none" }}
                    onChange={fileChangeHandler}
                />
                <Button
                    onlyText
                    width={"64px"}
                    height={"32px"}
                    bgColor={"var(--color-Button1)"}
                    hoverBgColor={"var(--color-ButtonHover1)"}
                    fontColor={"var(--color-White)"}
                    buttonText={"업로드"}
                    onClick={(e) => {
                        fileInputRef.current.value = null;
                        fileInputRef.current.click();
                    }}
                    disabled={fileUploadStatus === "loading" || isReadonlyUser(props?.user)}
                />
            </FileUploadText>
            <FileUploadText style={{ marginTop: "24px" }}>버튼을 누르거나</FileUploadText>
            <FileUploadText>PDF 파일을 이곳에 드래그하세요.</FileUploadText>
            <FileUploadText style={{ marginTop: "32px" }}>PDF 파일을 검색할 수도 있습니다.</FileUploadText>
            <FileSearchButtonWrap>
                {props.reference?.link?.doi && (
                    <Link to={props.reference.link.doi} target={"_blank"}>
                        <FileSearchButton onlyText buttonText={"DOI"} />
                    </Link>
                )}
                <Link
                    to={"https://scholar.google.com/scholar?q=" + encodeURIComponent(props.reference?.bib_json?.title)}
                    target={"_blank"}
                >
                    <FileSearchButton onlyText buttonText={"Google"} />
                </Link>
                {props.reference?.link?.scienceon_content_url && (
                    <Link to={props.reference?.link?.scienceon_content_url} target={"_blank"}>
                        <FileSearchButton onlyText buttonText={"ScienceON"} />
                    </Link>
                )}
                {props.reference?.link?.scienceon_fulltext_url && (
                    <Link to={props.reference?.link?.scienceon_fulltext_url} target={"_blank"}>
                        <FileSearchButton onlyText buttonText={"PDF"} />
                    </Link>
                )}
            </FileSearchButtonWrap>
        </FileUploadSection>
    );
}

export default PDFChatFileUploadView;
